import { language } from './../../core/models/merchants';
import { transition } from '@angular/animations';
import { CommonModule } from "@angular/common";
import { Component, inject, OnInit, signal } from "@angular/core";
import {
	FormBuilder,
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
	Validators,
} from "@angular/forms";
import { SvgIconComponent } from "angular-svg-icon";
import { AvatarModule } from "primeng/avatar";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { DividerModule } from "primeng/divider";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputTextModule } from "primeng/inputtext";
import { SkeletonModule } from "primeng/skeleton";
import { ReadOnlyWithLabelComponent } from "../../core/components/read-only-with-label/read-only-with-label.component";
import { ToastService } from "../../core/services/toast.service";
import { UserService } from "../../core/services/user/user.service";
import {
	mustMatch,
	passwordComplexityValidator,
} from "../../core/utils/password-validators";
import { CognitoService } from "../../core/services/cognito.service";
import { switchMap } from "rxjs";
import { SelectChangeEvent, SelectModule } from "primeng/select";
import { SaveButtonStateService } from "../../core/services/save-button.service";
import { MerchantsService } from "../../core/services/merchants/merchants.service";
import { LanguageService } from "../../core/services/language/language.service";
import { Category } from "../../core/models/catalog";
import { TranslationService } from '../../core/services/translation.service';
@Component({
    selector: "app-profile",
    imports: [
        AvatarModule,
        ReadOnlyWithLabelComponent,
        DividerModule,
        ButtonModule,
        DialogModule,
        ReactiveFormsModule,
        CommonModule,
        FormsModule,
        SkeletonModule,
        InputTextModule,
        SvgIconComponent,
        InputIconModule,
        IconFieldModule,
        SelectModule,
    ],
    templateUrl: "./profile.component.html",
    styleUrl: "./profile.component.scss"
})
export class ProfileComponent implements OnInit {
	userInfo: any;
	isDialogOpen = false;
	headerText: string = $localize`:@@profile.change.password:Change password`;
	submitted = false;
	loading = false;
	inCorrectPwd = false;
	isSuccessDialogOpen = false;
	currPasswordVisible = false;
	newPasswordVisible = false;
	confPasswordVisible = false;
	profileFormGroup!: FormGroup;
	localeOptions =  signal<Category[]>([]);
	merchantLanguage = "";
	private readonly cognitoService = inject(CognitoService);
	private readonly languageService = inject(LanguageService);
	private readonly translationService = inject(TranslationService);
	saveButtonState = inject(SaveButtonStateService);
	merchantService = inject(MerchantsService);
	constructor(
		private userService: UserService,
		private formBuilder: FormBuilder,
		private toastService: ToastService
	) {}

	ngOnInit() {
		this.userInfo = this.userService.loggedInUser();
		this.getUserDetail();
		this.localeOptions.set(this.merchantService.languages().map((lang) => {
			return {
				name: lang.name,
				value: lang.locale,
				id: lang.id,
			};
		}));
		this.profileFormGroup = this.formBuilder.group(
			{
				currentPassword: new FormControl("", [
					Validators.required,
					Validators.minLength(12),
				]),
				newPassword: new FormControl("", [
					Validators.required,
					Validators.minLength(12),
					passwordComplexityValidator(this.userInfo.userName),
				]),
				confirmPassword: new FormControl("", [Validators.required]),
			},
			{
				validators: mustMatch("newPassword", "confirmPassword"),
			},
		);
	}

	avatarName() {
		let fullName = "";
		if (this.userInfo.firstName)
			fullName = this.userInfo.firstName.charAt(0).toUpperCase();
		if (this.userInfo.lastName)
			fullName = fullName + this.userInfo.lastName.charAt(0).toUpperCase();
		return fullName;
	}

	get f() {
		return this.profileFormGroup.controls;
	}

	changePassword() {
		this.submitted = true;
		if (!this.profileFormGroup.valid) {
			return;
		}
		this.loading = true;
		this.inCorrectPwd = false;
		this.cognitoService
			.changePassword(
				this.userInfo.userName,
				this.profileFormGroup.value.currentPassword,
				this.profileFormGroup.value.confirmPassword,
			)
			.pipe(
				switchMap((data) => {
					return this.userService.passwordChangeNotify(this.userInfo.userName);
				}),
			)
			.subscribe({
				next: () => {
					this.loading = false;
					this.isDialogOpen = false;
					//TODO remove setTimeout, use RxJS delay instead
					setTimeout(() => {
						this.isSuccessDialogOpen = true;
					}, 10);
				},
				error: (error) => {
					if (error.message === "Incorrect username or password.") {
						this.inCorrectPwd = true;
					} else {
						this.toastService.showErrorToast("Error", error.message);
					}
					this.loading = false;
				},
			});
	}

	openDialog() {
		this.isDialogOpen = true;
		this.submitted = false;
		this.profileFormGroup.reset(this.profileFormGroup.value);
		this.f["currentPassword"].setValue("");
		this.f["newPassword"].setValue("");
		this.f["confirmPassword"].setValue("");
	}

	updateLanguage() {
		// this.saveButtonState.setSaving(true);
		const params = {
			...this.userInfo,
			locale: this.merchantLanguage,
		};
		const selectedLang: Category | undefined = this.localeOptions().find(lang => lang.value === this.merchantLanguage);
		this.userService
			.updateUserPreference(this.userInfo.userId, params, true)
			.subscribe({
				next: (data) => {
					this.userInfo = params;
					this.saveButtonState.setSaving(false);
					this.translationService.setTranslations(this.merchantLanguage, selectedLang?.id);
				},
				error: (error) => {
					this.saveButtonState.setSaving(false);
				},
			});
	}

	getUserDetail(){
		this.userService.getUserDetail(this.userInfo.userId).subscribe((data) => {
			this.userInfo = data;
			this.merchantLanguage = this.userInfo.locale.replace(/_/g, "-");
		});

	}

	cancel() {
		this.merchantLanguage = this.userInfo.locale;
	}
}
